import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from 'hooks/auth/useAuth'
import { ClockInReminder } from 'components/ClockInReminder'
import { DeviceAccessPrompt } from 'components/DeviceAccessPrompt'
import Footer from 'components/Footer'
import { getSuperUserPages, clientPages, dealerPages, bankPages } from 'router/pages'
import { Navigation } from 'types/navigation'
import { Outlet } from 'react-router-dom'
import { CoreAppBar } from './components/CoreAppBar'
import { CoreBottomNav } from './components/CoreBottomNav'
import { CoreDrawer } from './components/CoreDrawer'
import { MainLoading } from '@/components/Loading/MainLoading'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { useNavDensity } from 'contexts/NavDensityContext'
import { PageTabsProvider, usePageTabsContext } from 'contexts/PageTabsContext'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { ASSISTANT_FEATURE_FLAG_ID, HUB_SIDEBAR_FEATURE_FLAG_ID } from 'constants/featureFlags'
import { AssistantDrawer } from 'components/assistant'
import { assistantRoutesFromNavigation } from 'components/assistant/assistantRoutes'
import {
  APPBAR_HEIGHT,
  APPBAR_HEIGHT_DESKTOP,
  BOTTOM_NAV_HEIGHT,
  CHROME_GAP,
  DRAWER_COMPACT,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
} from 'core/theme.tokens'

const CoreLayoutInner: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const navigate = useNavigate()
  const { userInfo, currentUser, isLoading } = useAuth()
  const isMobile = useIsMobile()
  const { density, setDensity } = useNavDensity()
  const pageTabs = usePageTabsContext()
  const hubSidebarEnabled = useFeatureFlag(HUB_SIDEBAR_FEATURE_FLAG_ID)
  const assistantEnabled = useFeatureFlag(ASSISTANT_FEATURE_FLAG_ID)
  const isAdminRole = userInfo?.role === 'admin' || userInfo?.role === 'babyAdmin'
  /** Chatbot is admin-only (not babyAdmin). */
  const canUseAssistant = userInfo?.role === 'admin'
  const hubMode = hubSidebarEnabled && isAdminRole

  useEffect(() => {
    if (isLoading) return
    if (!currentUser) {
      navigate('/login')
    }
  }, [currentUser, isLoading, navigate])

  const handleMenuClick = () => {
    if (!isMobile) return
    setMobileOpen(open => !open)
  }

  const superuserPages = getSuperUserPages()

  const getNavigationItems = (): Navigation => {
    switch (userInfo?.role) {
      case 'admin':
        return superuserPages
      case 'babyAdmin':
        return superuserPages
      case 'client':
        return clientPages
      case 'dealer':
        return dealerPages
      case 'bankAgent':
        return bankPages
      default:
        return []
    }
  }

  if (isLoading || !currentUser) {
    return <MainLoading />
  }

  if (userInfo?.needsInitialPasswordSet) {
    return <Navigate to='/needs-reset-password' />
  }

  if (userInfo?.role === 'client' && userInfo.needsOnboarding === true) {
    return <Navigate to='/client-onboarding' />
  }

  if (userInfo?.role === 'bankAgent' && userInfo.needsOnboarding === true) {
    return <Navigate to='/bank-onboarding' />
  }

  const navigationItems: Navigation = getNavigationItems()
  const assistantRoutes = assistantRoutesFromNavigation(navigationItems)
  const showBottomNav = isMobile && Boolean(pageTabs?.config && pageTabs.config.tabs.length > 0)
  const chromeTop = isMobile
    ? `calc(${SAFE_AREA_TOP} + ${APPBAR_HEIGHT}px)`
    : `calc(${CHROME_GAP}px + ${SAFE_AREA_TOP} + ${APPBAR_HEIGHT_DESKTOP}px + ${CHROME_GAP}px)`
  const overlayOpen = !isMobile && density === 'comfortable'

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <CoreAppBar
        expanded={isMobile ? mobileOpen : density === 'comfortable'}
        onMenuClick={handleMenuClick}
      />
      {overlayOpen ? (
        <Box
          onClick={() => setDensity('compact')}
          aria-hidden
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: theme => theme.zIndex.drawer - 1,
            // Soft dim while expanded. Clicking the page collapses the rail.
            backgroundColor: 'rgba(15, 23, 42, 0.24)',
          }}
        />
      ) : null}
      <CoreDrawer
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        navigationItems={navigationItems}
        setMobileOpen={setMobileOpen}
        hubMode={hubMode}
      />
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          marginLeft: isMobile ? 0 : `${CHROME_GAP}px`,
          marginRight: isMobile ? 0 : `${CHROME_GAP}px`,
          marginTop: chromeTop,
          pt: { xs: 1, sm: 2 },
          pr: { xs: 1, sm: 2 },
          pl: isMobile ? 1 : `${DRAWER_COMPACT + CHROME_GAP}px`,
          pb: showBottomNav
            ? `calc(${BOTTOM_NAV_HEIGHT}px + ${SAFE_AREA_BOTTOM})`
            : isMobile
              ? SAFE_AREA_BOTTOM
              : `calc(${CHROME_GAP}px + ${SAFE_AREA_BOTTOM})`,
          minHeight: isMobile
            ? `calc(100dvh - ${SAFE_AREA_TOP} - ${APPBAR_HEIGHT}px)`
            : `calc(100dvh - ${CHROME_GAP}px - ${SAFE_AREA_TOP} - ${APPBAR_HEIGHT_DESKTOP}px - ${CHROME_GAP}px)`,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        <DeviceAccessPrompt />
        <ClockInReminder />
        <Box sx={{ flex: 1 }}>
          <Outlet />
        </Box>
        {!isMobile && <Footer />}
      </Box>
      {showBottomNav ? <CoreBottomNav /> : null}
      {assistantEnabled && canUseAssistant ? <AssistantDrawer routes={assistantRoutes} /> : null}
    </Box>
  )
}

export const CoreLayout: React.FC = () => (
  <PageTabsProvider>
    <CoreLayoutInner />
  </PageTabsProvider>
)

export default CoreLayout
